// autoSaveStore.js - Enhanced store for auto-save functionality
import { create } from "zustand";
import autoSaveService from '../services/AutoSaveService';
import changeDetector from '../services/ChangeDetector';
import savePerformanceMonitor from '../services/SavePerformanceMonitor';

const useAutoSaveStore = create((set, get) => ({
  // Auto-save state
  saveStatus: new Map(), // subcategoryId -> { status, lastSaved, error, retryCount }
  changeQueue: new Map(), // subcategoryId -> { type, data, timestamp }
  isAutoSaveEnabled: true,
  autoSaveDelay: 2000, // 2 seconds
  
  // Performance metrics
  performanceMetrics: {
    totalSaves: 0,
    successfulSaves: 0,
    failedSaves: 0,
    averageSaveTime: 0,
    lastSaveTime: null
  },

  // Initialize auto-save
  initializeAutoSave: () => {
    // Listen for auto-save events
    window.addEventListener('autosave', (event) => {
      const { type, subcategoryId, changeType, error, duration } = event.detail;
      
      switch (type) {
        case 'success':
          get().handleSaveSuccess(subcategoryId, changeType, duration);
          break;
        case 'error':
          get().handleSaveError(subcategoryId, changeType, error, duration);
          break;
        case 'saved':
          get().handleSaveComplete(subcategoryId);
          break;
      }
    });

    // Listen for performance issues
    window.addEventListener('autosave-performance-issue', (event) => {
      console.warn('Auto-save performance issue:', event.detail);
    });
  },

  // Mark a change for auto-save
  markChange: (subcategoryId, changeType, data) => {
    const { isAutoSaveEnabled } = get();
    
    if (!isAutoSaveEnabled) {
      return;
    }

    // Check if change is significant
    const isSignificant = changeDetector.hasSignificantChange(subcategoryId, data, changeType);

    if (!isSignificant) {
      return;
    }

    // Add to change queue
    set(state => ({
      changeQueue: new Map(state.changeQueue).set(subcategoryId, {
        type: changeType,
        data,
        timestamp: Date.now()
      }),
      saveStatus: new Map(state.saveStatus).set(subcategoryId, {
        status: 'pending',
        lastSaved: null,
        error: null,
        retryCount: 0
      })
    }));

    // Schedule auto-save
    autoSaveService.scheduleAutoSave(subcategoryId, changeType, data);
    
    // Track user interaction
    savePerformanceMonitor.trackUserInteraction('content_change', {
      subcategoryId,
      changeType,
      timestamp: Date.now()
    });
  },

  // Handle successful save
  handleSaveSuccess: (subcategoryId, changeType, duration) => {
    set(state => {
      const newSaveStatus = new Map(state.saveStatus);
      newSaveStatus.set(subcategoryId, {
        status: 'saved',
        lastSaved: Date.now(),
        error: null,
        retryCount: 0
      });

      const newMetrics = {
        ...state.performanceMetrics,
        totalSaves: state.performanceMetrics.totalSaves + 1,
        successfulSaves: state.performanceMetrics.successfulSaves + 1,
        lastSaveTime: Date.now()
      };

      // Update average save time
      if (state.performanceMetrics.totalSaves > 0) {
        const totalTime = state.performanceMetrics.averageSaveTime * state.performanceMetrics.totalSaves;
        newMetrics.averageSaveTime = (totalTime + duration) / newMetrics.totalSaves;
      } else {
        newMetrics.averageSaveTime = duration;
      }

      return {
        saveStatus: newSaveStatus,
        performanceMetrics: newMetrics
      };
    });

    // Track performance
    savePerformanceMonitor.trackSave({
      subcategoryId,
      changeType,
      duration,
      success: true,
      queueSize: get().changeQueue.size
    });
  },

  // Handle save error
  handleSaveError: (subcategoryId, changeType, error, duration) => {
    set(state => {
      const newSaveStatus = new Map(state.saveStatus);
      const currentStatus = newSaveStatus.get(subcategoryId) || {};
      
      newSaveStatus.set(subcategoryId, {
        status: 'error',
        lastSaved: currentStatus.lastSaved,
        error: error,
        retryCount: (currentStatus.retryCount || 0) + 1
      });

      const newMetrics = {
        ...state.performanceMetrics,
        totalSaves: state.performanceMetrics.totalSaves + 1,
        failedSaves: state.performanceMetrics.failedSaves + 1,
        lastSaveTime: Date.now()
      };

      return {
        saveStatus: newSaveStatus,
        performanceMetrics: newMetrics
      };
    });

    // Track performance
    savePerformanceMonitor.trackSave({
      subcategoryId,
      changeType,
      duration,
      success: false,
      error,
      queueSize: get().changeQueue.size
    });
  },

  // Handle save complete
  handleSaveComplete: (subcategoryId) => {
    set(state => {
      const newChangeQueue = new Map(state.changeQueue);
      newChangeQueue.delete(subcategoryId);

      return {
        changeQueue: newChangeQueue
      };
    });
  },

  // Get save status for a subcategory
  getSaveStatus: (subcategoryId) => {
    return get().saveStatus.get(subcategoryId) || { 
      status: 'saved', 
      lastSaved: null, 
      error: null, 
      retryCount: 0 
    };
  },

  // Check if there are pending changes
  hasPendingChanges: () => {
    return get().changeQueue.size > 0;
  },

  // Get pending changes count
  getPendingChangesCount: () => {
    return get().changeQueue.size;
  },

  // Force save all pending changes
  forceSaveAll: async () => {
    await autoSaveService.forceSaveAll();
  },

  // Check if there are any pending saves (safety check)
  hasPendingSaves: () => {
    return autoSaveService.hasPendingSaves();
  },

  // Get pending save count (safety check)
  getPendingSaveCount: () => {
    return autoSaveService.getPendingSaveCount();
  },

  // Toggle auto-save
  toggleAutoSave: () => {
    set(state => ({
      isAutoSaveEnabled: !state.isAutoSaveEnabled
    }));
  },

  // Set auto-save delay
  setAutoSaveDelay: (delay) => {
    set({ autoSaveDelay: delay });
    autoSaveService.debounceDelay = delay;
  },

  // Get performance summary
  getPerformanceSummary: () => {
    const storeMetrics = get().performanceMetrics;
    const serviceMetrics = autoSaveService.getMetrics();
    const monitorSummary = savePerformanceMonitor.getPerformanceSummary();

    return {
      store: storeMetrics,
      service: serviceMetrics,
      monitor: monitorSummary,
      isHealthy: monitorSummary.isHealthy
    };
  },

  // Reset all auto-save state
  resetAutoSave: () => {
    set({
      saveStatus: new Map(),
      changeQueue: new Map(),
      performanceMetrics: {
        totalSaves: 0,
        successfulSaves: 0,
        failedSaves: 0,
        averageSaveTime: 0,
        lastSaveTime: null
      }
    });

    autoSaveService.clearAll();
    changeDetector.clearAll();
    savePerformanceMonitor.reset();
  },

  // Get save status for all subcategories
  getAllSaveStatuses: () => {
    const saveStatus = get().saveStatus;
    const statuses = {};
    
    for (const [subcategoryId, status] of saveStatus) {
      statuses[subcategoryId] = status;
    }
    
    return statuses;
  },

  // Check if a specific subcategory has unsaved changes
  hasUnsavedChanges: (subcategoryId) => {
    const changeQueue = get().changeQueue;
    return changeQueue.has(subcategoryId);
  },

  // Get last save time for a subcategory
  getLastSaveTime: (subcategoryId) => {
    const status = get().getSaveStatus(subcategoryId);
    return status.lastSaved;
  },

  // Get error for a subcategory
  getSaveError: (subcategoryId) => {
    const status = get().getSaveStatus(subcategoryId);
    return status.error;
  },

  // Clear error for a subcategory
  clearSaveError: (subcategoryId) => {
    set(state => {
      const newSaveStatus = new Map(state.saveStatus);
      const currentStatus = newSaveStatus.get(subcategoryId);
      
      if (currentStatus) {
        newSaveStatus.set(subcategoryId, {
          ...currentStatus,
          error: null,
          status: 'saved'
        });
      }

      return { saveStatus: newSaveStatus };
    });
  },

  // Export metrics for debugging
  exportMetrics: () => {
    return {
      store: get().performanceMetrics,
      service: autoSaveService.getMetrics(),
      monitor: savePerformanceMonitor.exportMetrics(),
      changeQueue: Array.from(get().changeQueue.entries()),
      saveStatuses: Array.from(get().saveStatus.entries())
    };
  }
}));

export default useAutoSaveStore;
